import { InfoOutlined } from '@mui/icons-material';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { getMembers } from 'src/shared/services/memberService';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { Colors } from 'src/shared/components/styles/colors';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { useDashboardFilter } from '../dashboardFilterContext';
import { WidgetInstance } from '../types';
import { currentPeriodSub, formatPeriodSubLabel, Period, periodRange, previousPeriodRange, TrendChip } from './periodHelpers';

export type NewMembersByPeriodConfig = {
  period: Period;
};

const fetchCount = (filter: MemberSearchFilter): Promise<number> =>
  getMembers(0, 1, 'memberNr', 'desc', filter, [], undefined).then((res) => res.data.totalItems);

type StatusBreakdown = { paid: number; unpaid: number; interested: number; exited: number };

const STATUS_LEGEND: { key: keyof StatusBreakdown; status: MemberStatusEnum; color: string }[] = [
  { key: 'paid', status: MemberStatusEnum.Paid, color: Colors.ChipColors.Green },
  { key: 'unpaid', status: MemberStatusEnum.NotPaid, color: Colors.ChipColors.Red },
  { key: 'interested', status: MemberStatusEnum.NewMember, color: Colors.ChipColors.Yellow },
  { key: 'exited', status: MemberStatusEnum.NotMember, color: '#9e9e9e' },
];

type Props = {
  instance: WidgetInstance;
};

export const NewMembersByPeriodWidget = ({ instance }: Props) => {
  const { t } = useTranslation(['dashboard']);
  const config = (instance.config as NewMembersByPeriodConfig | undefined) ?? { period: 'thisMonth' };
  const isMounted = useIsMounted();
  const { associationId, districtId } = useDashboardFilter();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [count, setCount] = useState<number>();
  const [previousCount, setPreviousCount] = useState<number>();
  const [breakdown, setBreakdown] = useState<StatusBreakdown>();

  useEffect(() => {
    setLoading(true);
    setHasError(false);
    const baseFilter = {
      associationId,
      districtIds: districtId ? [districtId] : undefined,
    };
    const currentRange = periodRange(config.period);
    const prevRange = previousPeriodRange(config.period);
    Promise.all([
      ...STATUS_LEGEND.map((s) =>
        fetchCount({ ...baseFilter, admDateStart: currentRange.from, admDateEnd: currentRange.to, status: [s.status] } as MemberSearchFilter),
      ),
      fetchCount({ ...baseFilter, admDateStart: prevRange.from, admDateEnd: prevRange.to }),
    ])
      .then((results) => {
        if (!isMounted()) return;
        const [paid, unpaid, interested, exited, prev] = results;
        setBreakdown({ paid, unpaid, interested, exited });
        setCount(paid + unpaid + interested + exited);
        setPreviousCount(prev);
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        if (isMounted()) setHasError(true);
      })
      .finally(() => {
        if (isMounted()) setLoading(false);
      });
  }, [config.period, associationId, districtId, isMounted]);

  return (
    <Stack sx={{ p: 2, height: '100%' }} spacing={1.25}>
      <Stack spacing={0.25}>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <HeaderSubTitle label={t('dashboard:newMembersByPeriod.header')} />
          <Tooltip arrow title={t('dashboard:newMembersByPeriod.info')}>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', mb: 0.5 }} />
          </Tooltip>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
          {t(`dashboard:period.labels.${config.period}`)} ({formatPeriodSubLabel(currentPeriodSub(config.period), t)})
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
        {(() => {
          if (loading) return <Skeleton variant='text' sx={{ fontSize: 36, width: '60%' }} />;
          if (hasError) return <Typography sx={{ color: 'error.main', fontSize: 13 }}>{t('dashboard:fetchError')}</Typography>;
          return (
            <>
              <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {(count ?? 0).toLocaleString('sv-SE')}
                <Typography component='span' sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
                  {t('dashboard:piciesShort')}
                </Typography>
              </Typography>
              <TrendChip current={count ?? 0} previous={previousCount ?? 0} period={config.period} />
              {breakdown && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 0.5, mt: 0.5 }}>
                  {STATUS_LEGEND.map((s) => (
                    <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{breakdown[s.key]}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t(`dashboard:newMembersByPeriod.statusBreakdown.${s.key}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Stack>
  );
};
